import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Salad, AlertTriangle, TestTube, Lightbulb } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

export const FoodEducation = () => {
  const { API, token } = useAuth();
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGuides();
  }, []);

  const fetchGuides = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/food-guides`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGuides(response.data);
    } catch (error) {
      console.error('Error fetching guides:', error);
    } finally {
      setLoading(false);
    }
  };

  const safeGuides = guides.filter(g => g.category === 'safe-foods');
  const avoidGuides = guides.filter(g => g.category === 'foods-to-avoid');
  const testGuides = guides.filter(g => g.category === 'foods-to-test');

  const renderFoodList = (foodsArray, isAvoid = false) => (
    <div className="grid gap-4">
      {foodsArray.map((food, i) => (
        <Card key={i} className="bg-white rounded-xl border border-stone-100 p-4 hover:shadow-md transition-shadow">
          <div className="space-y-2">
            <h4 className="font-nunito font-bold text-[#2D2D2D]">{food.name}</h4>
            {food.benefits && <p className="text-sm text-primary">{food.benefits}</p>}
            <p className="text-sm text-[#57534E] italic">{food.notes}</p>
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-8" data-testid="food-education">
      <div className="bg-gradient-to-br from-secondary/20 to-primary/10 rounded-xl p-8 md:p-12">
        <div className="flex items-center gap-4 mb-4">
          <Salad className="w-12 h-12 text-primary" strokeWidth={1.5} />
          <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">Food Education</h1>
        </div>
        <p className="text-lg text-[#57534E] leading-relaxed">
          Comprehensive guide to foods for histamine intolerance, MCAS, and autoimmune conditions
        </p>
      </div>

      {loading ? (
        <div className="text-center py-12"><p className="text-[#A8A29E]">Loading food guides...</p></div>
      ) : (
        <Tabs defaultValue="safe" className="space-y-6">
          <TabsList className="grid w-full grid-cols-3 bg-white rounded-xl border border-stone-200 p-2">
            <TabsTrigger value="safe" className="rounded-lg data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="safe-foods-tab">
              <Salad className="w-4 h-4 mr-2" />Safe Foods
            </TabsTrigger>
            <TabsTrigger value="avoid" className="rounded-lg data-[state=active]:bg-accent data-[state=active]:text-accent-foreground" data-testid="avoid-foods-tab">
              <AlertTriangle className="w-4 h-4 mr-2" />Foods to Avoid
            </TabsTrigger>
            <TabsTrigger value="test" className="rounded-lg data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground" data-testid="test-foods-tab">
              <TestTube className="w-4 h-4 mr-2" />Foods to Test
            </TabsTrigger>
          </TabsList>

          <TabsContent value="safe" className="space-y-6">
            {safeGuides.map(guide => (
              <Card key={guide.id} className="bg-white rounded-xl border border-stone-100 p-8">
                <div className="flex items-start gap-3 mb-4">
                  <Lightbulb className="w-8 h-8 text-primary flex-shrink-0" />
                  <div>
                    <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-2">{guide.title}</h2>
                    <p className="text-sm text-primary font-semibold mb-2">For: {guide.condition}</p>
                    <p className="text-[#57534E] leading-relaxed mb-6">{guide.description}</p>
                  </div>
                </div>
                {renderFoodList(guide.foods)}
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="avoid" className="space-y-6">
            {avoidGuides.map(guide => (
              <Card key={guide.id} className="bg-white rounded-xl border border-stone-100 p-8">
                <div className="flex items-start gap-3 mb-4">
                  <AlertTriangle className="w-8 h-8 text-accent flex-shrink-0" />
                  <div>
                    <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-2">{guide.title}</h2>
                    <p className="text-sm text-accent font-semibold mb-2">For: {guide.condition}</p>
                    <p className="text-[#57534E] leading-relaxed mb-6">{guide.description}</p>
                  </div>
                </div>
                {renderFoodList(guide.foods, true)}
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="test" className="space-y-6">
            {testGuides.map(guide => (
              <Card key={guide.id} className="bg-white rounded-xl border border-stone-100 p-8">
                <div className="flex items-start gap-3 mb-4">
                  <TestTube className="w-8 h-8 text-secondary-foreground flex-shrink-0" />
                  <div>
                    <h2 className="text-2xl font-nunito font-bold text-[#2D2D2D] mb-2">{guide.title}</h2>
                    <p className="text-sm text-secondary-foreground font-semibold mb-2">For: {guide.condition}</p>
                    <p className="text-[#57534E] leading-relaxed mb-6">{guide.description}</p>
                  </div>
                </div>
                {renderFoodList(guide.foods)}
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default FoodEducation;
